export type TeacherItem = {
  namespace: string;
  display_name: string;
  latest_sha256: string;
  // 老师可覆盖的文体集合（由 Worker 侧从规则 tags 里提取）
  genres: string[];
};

export type TeachersResponse = {
  domain_slug: string;
  teachers: TeacherItem[];
};

export type DiagnoseRequest = {
  domain: string;
  domain_slug: string;
  namespace: string;
  grade?: string;
  title?: string;
  target_genre?: string;
  essay_text: string;
};

export type DiagnoseResponse = {
  meta: Record<string, unknown>;
  scan_results: unknown[];
  report: Record<string, unknown>;
};

// 统计用的浏览器实例标识（用户无感）：仅用于去重 DAU/WAU 等，不影响业务逻辑。
// - 优先持久化到 localStorage（同一浏览器稳定）；
// - 若 localStorage 不可用（隐私模式/禁用存储），降级为内存态临时 id（不抛错、不提示）。
const CLIENT_ID_STORAGE_KEY = "m2s_client_id_v1";
let memClientId: string | null = null;

function randomId(): string {
  // 优先使用标准 API（更快更稳）
  if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
    return (crypto as any).randomUUID();
  }
  // 兼容不支持 randomUUID 的环境
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = (Math.random() * 256) | 0;
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function getClientId(): string {
  if (memClientId) return memClientId;
  try {
    const existing = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existing && existing.trim()) {
      memClientId = existing.trim();
      return memClientId;
    }
    const id = randomId();
    localStorage.setItem(CLIENT_ID_STORAGE_KEY, id);
    memClientId = id;
    return id;
  } catch {
    // localStorage 不可用：只在内存中保持本次会话一致性
    memClientId = randomId();
    return memClientId;
  }
}

// 根据 HTTP 状态码返回友好的错误信息
function getFriendlyErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return "请求格式有误，请检查输入内容后重试。";
    case 401:
    case 403:
      return "邀请码无效或已过期，请检查后重新输入。";
    case 404:
      return "找不到请求的服务，请稍后重试。";
    case 408:
      return "请求超时了，老师思考得太久啦！请稍后重试。";
    case 429:
      return "请求太频繁了，请休息一下再试。";
    case 500:
      return "服务器开小差了，请稍后重试。如果持续出现，可以换一篇作文试试。";
    case 502:
    case 503:
    case 504:
      return "服务暂时不可用，可能正在维护中，请稍后重试。";
    default:
      return status >= 500
        ? "服务器遇到了问题，请稍后重试。"
        : "请求出错了，请稍后重试。";
  }
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const clientId = getClientId();
  const headers = new Headers(init?.headers || {});
  // 仅用于后台统计去重；服务端会对缺失情况自动降级，不影响用户体验
  if (!headers.has("X-Client-Id")) headers.set("X-Client-Id", clientId);

  let resp: Response;
  try {
    resp = await fetch(input, { ...init, headers });
  } catch (e) {
    // 网络错误（如断网、DNS 失败等）
    console.error("[API] 网络请求失败:", e);
    throw new Error("网络连接失败，请检查网络后重试。");
  }

  const text = await resp.text();
  const ct = resp.headers.get("content-type") || "";
  const t = text.trimStart();
  const looksLikeHtml = t.startsWith("<!doctype") || t.startsWith("<html") || t.startsWith("<");

  // 非 2xx 时：给用户友好提示，技术细节记录到控制台
  if (!resp.ok) {
    const preview = t.replace(/\s+/g, " ").slice(0, 240);
    console.error(`[API] HTTP ${resp.status}; content-type=${ct}; preview="${preview}"`);
    throw new Error(getFriendlyErrorMessage(resp.status));
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    const preview = t.replace(/\s+/g, " ").slice(0, 160);
    console.error(`[API] JSON 解析失败; content-type=${ct}; preview="${preview}"`);
    throw new Error(
      looksLikeHtml
        ? "服务返回了异常内容，请稍后重试。"
        : "服务返回的数据格式有误，请稍后重试。"
    );
  }
}

export async function apiTeachers(inviteCode: string): Promise<TeachersResponse> {
  return await fetchJson<TeachersResponse>("/api/teachers", {
    headers: { "X-Invite-Code": inviteCode },
  });
}

export async function apiDiagnose(inviteCode: string, body: DiagnoseRequest): Promise<DiagnoseResponse> {
  return await fetchJson<DiagnoseResponse>("/api/diagnose", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Invite-Code": inviteCode,
    },
    body: JSON.stringify(body),
  });
}

export async function apiReportRtf(inviteCode: string, body: DiagnoseResponse): Promise<Blob> {
  let resp: Response;
  try {
    resp = await fetch("/api/report.rtf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Invite-Code": inviteCode,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.error("[API] 下载报告网络请求失败:", e);
    throw new Error("网络连接失败，请检查网络后重试。");
  }

  if (!resp.ok) {
    const text = await resp.text();
    console.error(`[API] 下载报告失败 HTTP ${resp.status}:`, text.slice(0, 200));
    throw new Error(
      resp.status === 500
        ? "生成报告时出错了，请稍后重试。"
        : getFriendlyErrorMessage(resp.status)
    );
  }
  return await resp.blob();
}

